import { Alert, App, Button, Col, Flex, Row, Spin, Typography, Popconfirm, Tag, Tooltip, Card } from "antd";
import { motion } from "framer-motion";
import { ExamContent } from "../../components/exam-period/correction/ExamContent";
import { QuestionsSider } from "../../components/exam-period/correction/QuestionsSider";
import { GradingLockIndicator } from "../../components/grading/GradingLockIndicator";
import { useExamStore } from "@store";
import { useParams, useNavigate } from "react-router-dom";
import { useGetExamInstanceTree, useStartGradingSession, useCompleteGradingSession, useRecalculateExamInstance, useGetGradingSummary } from "@hooks";
import { Trans, useTranslation } from "react-i18next";
import { useEffect, useState, useRef } from "react";
import { CheckCircle, Hourglass, RefreshCcw } from "lucide-react";

export const ExamPeriodCorrectionPage = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const questionsLength = useExamStore((state) => state.questionsLength());
  const { t } = useTranslation();
  const setExamInstanceTree = useExamStore((state) => state.setExamInstanceTree);
  const { examInstanceId } = useParams();
  const { data, isLoading } = useGetExamInstanceTree(examInstanceId);
  const currentQuestionIndex = useExamStore((state) =>
    state.gradingIndex
  );
  const startGradingSession = useStartGradingSession();
  const completeGradingSession = useCompleteGradingSession();
  const recalculateExamInstance = useRecalculateExamInstance();
  const [sessionStarted, setSessionStarted] = useState(false);
  const [sessionCompleted, setSessionCompleted] = useState(false);
  const [sessionFailed, setSessionFailed] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const sessionInitialized = useRef(false);
  const gradingSummary = useGetGradingSummary(examInstanceId, { enabled: showSummary });

  useEffect(() => {
    if (data) {
      setExamInstanceTree(data);
      // Note: Initial question index is set by QuestionsSider component
      // which handles the filter logic for manual grading questions
    }
  }, [data]);

  // Auto-start grading session when page loads (only once)
  useEffect(() => {
    if (examInstanceId && !sessionInitialized.current) {
      sessionInitialized.current = true;
      startGradingSession.mutate(examInstanceId, {
        onSuccess: (result) => {
          setSessionStarted(true);
          if (result.wasAlreadyInProgress) {
            message.info(t("grading.sessionResumed"));
          } else {
            message.success(t("grading.sessionStarted"));
          }
        },
        onError: () => {
          setSessionFailed(true);
          message.error(t("grading.sessionStartFailed"));
        },
      });
    }
  }, [examInstanceId]);

  const handleCompleteGrading = () => {
    if (examInstanceId) {
      completeGradingSession.mutate(examInstanceId, {
        onSuccess: () => {
          setSessionStarted(false);
          setSessionCompleted(true);
          message.success(t("grading.sessionCompleted"));
        },
        onError: () => {
          message.error(t("grading.sessionCompleteFailed"));
        },
      });
    }
  };

  const handleRecalculate = () => {
    if (examInstanceId) {
      recalculateExamInstance.mutate(examInstanceId, {
        onSuccess: () => {
          setShowSummary(true);
        },
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 1 }}
    >
      <Spin spinning={isLoading}>
        <div
          style={{ display: "flex", flexDirection: "row", marginBottom: 10 }}
        >
          <div
            style={{
              width: 3,
              backgroundColor: "#F1B80E",
              borderRadius: 100,
              marginRight: 12,
            }}
          />
          <Typography.Title level={3} style={{ margin: 0 }}>
            {t("pages.examPeriods.examCorrection")} - {data?.title} ({`${data?.userExaminee?.firstName} ${data?.userExaminee?.lastName}`})
          </Typography.Title>
        </div>
        {examInstanceId && <GradingLockIndicator examInstanceId={examInstanceId} />}
        
        {sessionFailed && (
          <Alert
            type="error"
            showIcon
            message={t("grading.sessionStartFailed")}
            description={t("grading.sessionStartFailedDescription")}
            style={{ marginBottom: 12 }}
            action={
              <Button
                size="small"
                danger
                onClick={() => {
                  if (examInstanceId) {
                    setSessionFailed(false);
                    startGradingSession.mutate(examInstanceId, {
                      onSuccess: (result) => {
                        setSessionStarted(true);
                        setSessionFailed(false);
                        if (result.wasAlreadyInProgress) {
                          message.info(t("grading.sessionResumed"));
                        } else {
                          message.success(t("grading.sessionStarted"));
                        }
                      },
                      onError: () => {
                        setSessionFailed(true);
                        message.error(t("grading.sessionStartFailed"));
                      },
                    });
                  }
                }}
              >
                {t("grading.retrySession")}
              </Button>
            }
          />
        )}

        <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
          <Flex gap={8} align="center">
            {sessionCompleted ? (
              <>
                <Tag color="success" style={{ margin: 0, padding: '4px 12px', fontSize: 14 }}>
                  <Flex align="center" gap={6}>
                    <CheckCircle size={14} />
                    {t("grading.gradingComplete")}
                  </Flex>
                </Tag>
                <Button
                  type="primary"
                  onClick={() => navigate(-1)}
                >
                  {t("grading.backToList")}
                </Button>
              </>
            ) : (
              <Tooltip title={
                sessionStarted 
                  ? t("grading.completeSessionHint") 
                  : sessionFailed 
                    ? t("grading.completeSessionFailedHint") 
                    : t("grading.completeSessionDisabledHint")
              }>
                <Popconfirm
                  title={t("grading.completeConfirmTitle")}
                  description={t("grading.completeConfirmDescription")}
                  onConfirm={handleCompleteGrading}
                  okText={t("yes")}
                  cancelText={t("no")}
                  disabled={!sessionStarted}
                >
                  <Button
                    type="primary"
                    icon={<CheckCircle size={16} />}
                    loading={completeGradingSession.isPending}
                    disabled={!sessionStarted}
                    style={{ backgroundColor: sessionStarted ? '#52c41a' : undefined, borderColor: sessionStarted ? '#52c41a' : undefined }}
                  >
                    {t("grading.completeSession")}
                  </Button>
                </Popconfirm>
              </Tooltip>
            )}
            <Tooltip title={t("grading.recalculateHint")}>
              <Popconfirm
                title={t("grading.recalculateConfirmTitle")}
                description={t("grading.recalculateConfirmDescription")}
                onConfirm={handleRecalculate}
                okText={t("yes")}
                cancelText={t("no")}
              >
                <Button
                  icon={<RefreshCcw size={16} />}
                  loading={recalculateExamInstance.isPending}
                >
                  {t("grading.recalculate")}
                </Button>
              </Popconfirm>
            </Tooltip>
          </Flex>
          <Typography.Paragraph style={{ margin: 0 }}>
            <Trans i18nKey={"pages.examPeriods.questionsAnswered"} count={currentQuestionIndex}>
            Grading {{count: currentQuestionIndex}} out of {{length: questionsLength}} questions
            </Trans>

          </Typography.Paragraph>
        </Flex>
        {showSummary && gradingSummary.data && (
          <Card
            size="small"
            style={{ marginBottom: 16 }}
            styles={{ body: { padding: '12px 16px' } }}
          >
            <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
              <Flex align="center" gap={12}>
                <Tag
                  color={gradingSummary.data.hasPassed === true ? 'success' : gradingSummary.data.hasPassed === false ? 'error' : 'default'}
                  style={{ margin: 0, padding: '4px 16px', fontSize: 15, fontWeight: 600 }}
                >
                  {gradingSummary.data.hasPassed === true
                    ? t('grading.result.passed')
                    : gradingSummary.data.hasPassed === false
                      ? t('grading.result.failed')
                      : t('grading.result.pending')}
                </Tag>
                <Typography.Text>
                  {t('grading.result.score')}: <strong>{gradingSummary.data.realizedScore} / {gradingSummary.data.totalScore}</strong>
                </Typography.Text>
                <Typography.Text type="secondary">
                  ({t('grading.result.competencies')}: {gradingSummary.data.passedCompetencies} / {gradingSummary.data.totalCompetencies})
                </Typography.Text>
                {gradingSummary.data.ungradedQuestions > 0 && (
                  <Tooltip title={t('grading.result.ungradedQuestionsHint')}>
                    <Tag color="warning" style={{ margin: 0 }}>
                      <Flex align="center" gap={4}>
                        <Hourglass size={12} />
                        {t('grading.result.ungradedQuestions', { count: gradingSummary.data.ungradedQuestions })}
                      </Flex>
                    </Tag>
                  </Tooltip>
                )}
              </Flex>
              <Button size="small" type="text" onClick={() => setShowSummary(false)}>
                ✕
              </Button>
            </Flex>
            {gradingSummary.data.competencySummaries.length > 1 && (
              <Flex gap={16} style={{ marginTop: 8 }} wrap="wrap">
                {gradingSummary.data.competencySummaries.map((c) => {
                  const isFullyGraded = c.gradedQuestions === c.totalQuestions;
                  return (
                    <Tooltip
                      key={c.examInstanceGroupId}
                      title={
                        isFullyGraded
                          ? `${c.realizedScore} ${t('grading.result.points')}`
                          : t('grading.result.competencyNotFullyGraded', { graded: c.gradedQuestions, total: c.totalQuestions })
                      }
                    >
                      <Tag color={!isFullyGraded ? 'warning' : c.hasPassed ? 'success' : 'error'} style={{ margin: 0 }}>
                        <Flex align="center" gap={4}>
                          {!isFullyGraded && <Hourglass size={12} />}
                          {c.name}: {!isFullyGraded ? '…' : c.hasPassed ? '✓' : '✗'}
                        </Flex>
                      </Tag>
                    </Tooltip>
                  );
                })}
              </Flex>
            )}
          </Card>
        )}
        <Row gutter={16}>
          <Col span={6}>
            <QuestionsSider examInstanceTree={data} />
          </Col>
          <Col span={18}>
            <ExamContent 
              examInstanceId={examInstanceId} 
              examInstanceTree={data}
              gradingConfigurationId={data?.gradingConfigurationId}
            />
          </Col>
        </Row>
        <div
          style={{ display: "flex", justifyContent: "flex-end", marginTop: 15 }}
        ></div>
      </Spin>
    </motion.div>
  );
};
